import {
  FastLarge,
  MissingToothLarge,
  ModerateLarge,
  NoMovementLarge,
  NotTrackingLarge,
  PartiallyTrackingLarge,
  SlowLarge,
  TrackingLarge,
  TrackingNoMovementLarge,
  UnknownLarge
} from 'components/common/icons'
import NoSignificantMovementLarge from 'components/common/icons/treatmentTracker/teethTrackingLegend/NoSignificantMovementLarge'
import useScans from 'hooks/useScans'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { txTrackerOverTimeByScan as txTrackerOverTimeByScanMock } from '../mockData'
import { PROGRESS_TYPES, TeethTrackingStatuses } from '../txTrackerConsts'
import { getToothMovementPercentage } from './toothProgressUtils'
import MissingSpeedInfoLarge from 'components/common/icons/treatmentTracker/teethTrackingLegend/MissingSpeedInfoLarge'
import { getToothTrackingStatus as getToothTrackingStatusUtil } from './teethTrackingStatusUtils'
import { isRetentionStamp, isSameTrackingContext, readTrackingStamp } from '../trackingStampUtils'

export default () => {
  const { scanIdToNumberDict, getScanNumberByTimelineItemData } = useScans()

  const patientGrinScans = useSelector(state => state.patientsReducer.patient?.grinScans?.items || [])
  const { grinScanId } = useSelector(state => state.treatmentReducer.treatmentTrackerModal)
  const isTimelineV2 = useSelector(state => state.timelineReducer.isTimelineV2)

  const getAsteriskData = toothData => {
    if (!toothData) {
      return { shouldApplyAsterisk: false }
    }

    const { unknown, low_confidence, no_movement_planned } = toothData

    if (!unknown && low_confidence && !no_movement_planned) {
      return {
        shouldApplyAsterisk: true,
        // explanation: `The result is with low certainty. Please review the tooth if the outcome doesn't seem correct \n (unknown: ${unknown}, low_confidence: ${low_confidence}, no_movement_planned: ${no_movement_planned})`
        explanation: 'Low certainty'
      }
    }

    if (unknown && no_movement_planned) {
      return {
        shouldApplyAsterisk: true,
        // explanation: `No Movement Planned (unknown: ${unknown}, low_confidence: ${low_confidence}, no_movement_planned: ${no_movement_planned})`
        explanation: 'Unknown'
      }
    }

    if (unknown && !no_movement_planned) {
      return {
        shouldApplyAsterisk: false,
        // explanation: `The result could not be determined, please review the tooth manually \n (unknown: ${unknown}, low_confidence: ${low_confidence}, no_movement_planned: ${no_movement_planned})`
        explanation: 'Unknown'
      }
    }

    return { shouldApplyAsterisk: false }
  }

  const getToothTrackingStatus = ({ toothData, treatmentType }) => {
    return getToothTrackingStatusUtil({ toothData, treatmentType })
  }

  const grinScan = useMemo(() => patientGrinScans.find(scan => scan.id === grinScanId), [grinScanId, patientGrinScans])

  const txTrackerOverTimeByTeeth = useMemo(() => {
    let allTeethData = {}

    // The graph plots deviations against one baseline. Results from another tracking
    // context (retention vs active, or a different retention reference set) were measured
    // against different models, so mixing them in would fabricate movement.
    const currentScanStamp = readTrackingStamp(grinScan?.treatmentTrackerData)

    patientGrinScans
      .filter(
        grinScan =>
          !!grinScan.treatmentTrackerData &&
          grinScan.treatmentTrackerStatus === 'completed' &&
          isSameTrackingContext(readTrackingStamp(grinScan.treatmentTrackerData), currentScanStamp)
      )
      .forEach(grinScan => {
        const { teeth: currentScanTeethData } = JSON.parse(grinScan.treatmentTrackerData || '{}')

        Object.keys(currentScanTeethData).forEach(currTeethKey => {
          const currentTeethData = {
            ...currentScanTeethData[currTeethKey],
            createdAt: grinScan.createdAt,
            alignerNumber:
              !isRetentionStamp(currentScanStamp) && grinScan.applianceIndex > 0 ? grinScan.applianceIndex : null,
            scanNumber: isTimelineV2
              ? getScanNumberByTimelineItemData(grinScan.id)
              : scanIdToNumberDict[grinScan.id] + 1
          }

          if (!allTeethData?.[currTeethKey]) {
            allTeethData[currTeethKey] = [currentTeethData]
          } else {
            allTeethData[currTeethKey] = [...allTeethData[currTeethKey], currentTeethData]
          }
        })
      })

    return allTeethData
  }, [patientGrinScans, scanIdToNumberDict, getScanNumberByTimelineItemData, isTimelineV2, grinScan])

  const trackingStatuses = useMemo(
    () => ({
      [TeethTrackingStatuses.Tracking]: {
        borderColor: '#C0F7C5',
        dotBorderColor: '#C0F7C5',
        backgroundColor: '#D2FFD6',
        icon: <TrackingLarge />
      },
      [TeethTrackingStatuses.TrackingNoMovement]: {
        borderColor: '#C0F7C5',
        dotBorderColor: '#C0F7C5',
        backgroundColor: '#D2FFD6',
        icon: <TrackingNoMovementLarge />
      },
      [TeethTrackingStatuses.TrackingNoMovementPlanned]: {
        borderColor: '#C0F7C5',
        dotBorderColor: '#C0F7C5',
        backgroundColor: '#D2FFD6',
        icon: <TrackingNoMovementLarge />
      },
      [TeethTrackingStatuses.PartiallyTracking]: {
        borderColor: '#FFDBA4',
        dotBorderColor: '#FFDBA4',
        backgroundColor: '#FFECCF',
        icon: <PartiallyTrackingLarge />
      },
      [TeethTrackingStatuses.NotTracking]: {
        borderColor: '#F6B3B3',
        dotBorderColor: '#F6B3B3',
        backgroundColor: '#FFD0D0',
        icon: <NotTrackingLarge />
      },
      [TeethTrackingStatuses.Unknown]: {
        borderColor: '#CAD9FF',
        dotBorderColor: '#CAD9FF',
        backgroundColor: '#CAD9FF',
        icon: <UnknownLarge />
      },
      [TeethTrackingStatuses.Fast]: {
        icon: <FastLarge />,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: '#BDD3EE'
      },
      [TeethTrackingStatuses.Moderate]: {
        icon: <ModerateLarge />,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: '#BDD3EE'
      },
      [TeethTrackingStatuses.Slow]: {
        icon: <SlowLarge />,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: '#BDD3EE'
      },
      [TeethTrackingStatuses.NoMovement]: {
        icon: <NoMovementLarge />,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: 'transparent'
      },
      [TeethTrackingStatuses.NoSignificantMovement]: {
        icon: <NoSignificantMovementLarge />,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: '#CAD9FF'
      },
      [TeethTrackingStatuses.MissingTooth]: {
        icon: <MissingToothLarge />,
        borderColor: '#DBEAFF',
        dotBorderColor: '#DBEAFF'
      },
      [TeethTrackingStatuses.MissingSpeedInfo]: {
        icon: <MissingSpeedInfoLarge />,
        backgroundColor: '#DBEAFF',
        borderColor: 'transparent',
        dotBorderColor: 'transparent'
      },
      [TeethTrackingStatuses.NegligiblePlan]: {
        icon: <></>,
        backgroundColor: '#DAE7F7',
        borderColor: 'transparent',
        dotBorderColor: 'transparent'
      }
    }),
    []
  )

  const getToothMovementPrecantage = getToothMovementPercentage

  const getProgressFieldByProgressType = useCallback(
    ({ currentProgress, currentRotation, currentTranslation, progressType }) => {
      switch (progressType) {
        case PROGRESS_TYPES.Overall:
          return currentProgress
        case PROGRESS_TYPES.Rotation:
          return currentRotation
        case PROGRESS_TYPES.Translation:
          return currentTranslation
        default:
          return null
      }
    },
    []
  )

  return {
    getToothTrackingStatus,
    getAsteriskData,
    trackingStatuses,
    getToothMovementPrecantage,
    getProgressFieldByProgressType,
    txTrackerDataByTeeth:
      grinScan && Object.keys(txTrackerOverTimeByScanMock).includes(grinScan?.id)
        ? txTrackerOverTimeByScanMock[grinScanId]
        : txTrackerOverTimeByTeeth
  }
}
